import React, { useState, useEffect, useRef } from 'react';
import useAuth from '../../hooks/useAuth';
import kerrscreenFilesService from '../../services/kerrscreenFilesApi';
import './Dashboard.css';

const Dashboard = () => {
  const { user, hasPermission } = useAuth();
  const fileInputRef = useRef(null);

  // View state
  const [viewMode, setViewMode] = useState('grid');

  // Navigation state
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);

  // Data state
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [customerFilter, setCustomerFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');

  // Filter options
  const [customers, setCustomers] = useState([]);
  const [allTags, setAllTags] = useState([]);

  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // File drag state
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [draggingFile, setDraggingFile] = useState(null);

  // Modal state
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false);
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [linkJobModalOpen, setLinkJobModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [newName, setNewName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderCustomer, setNewFolderCustomer] = useState('');
  const [editCustomer, setEditCustomer] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [linkJobId, setLinkJobId] = useState('');

  // Pagination state
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 1 });

  // Stats state
  const [stats, setStats] = useState({ totalFiles: 0, totalSize: 0, storageLimit: 0 });

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchFiles();
    fetchStats();
  }, [currentFolderId, pagination.page]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFiles = async (page = pagination.page) => {
    try {
      setLoading(true);
      const response = await kerrscreenFilesService.getAll({
        folderId: currentFolderId,
        page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        customerName: customerFilter || undefined,
        tag: tagFilter || undefined,
        jobId: jobFilter || undefined,
      });
      const data = response.data;
      setFiles(data.files || []);
      if (data.pagination) setPagination(data.pagination);
      setError(null);
    } catch (err) {
      console.error('Error fetching art files:', err);
      setError('Failed to load art files.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await kerrscreenFilesService.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [custRes, tagRes] = await Promise.all([
        kerrscreenFilesService.getCustomers(),
        kerrscreenFilesService.getTags(),
      ]);
      setCustomers(custRes.data || []);
      setAllTags(tagRes.data || []);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchFiles(1);
  };

  const handleFilterChange = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchFiles(1);
  };

  // Folder navigation
  const openFolder = (folder) => {
    setCurrentFolderId(folder.id);
    setFolderPath([...folderPath, { id: folder.id, name: folder.original_name }]);
  };

  const navigateToFolder = (index) => {
    if (index === -1) {
      setCurrentFolderId(null);
      setFolderPath([]);
    } else {
      const folder = folderPath[index];
      setCurrentFolderId(folder.id);
      setFolderPath(folderPath.slice(0, index + 1));
    }
  };

  // Folder creation
  const openCreateFolderModal = () => {
    setCreateFolderModalOpen(true);
    setNewFolderName('');
    setNewFolderCustomer('');
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) {
      setError('Folder name cannot be empty');
      return;
    }
    try {
      await kerrscreenFilesService.createFolder({
        name: newFolderName,
        parentFolderId: currentFolderId,
        customerName: newFolderCustomer || undefined,
      });
      setSuccess('Folder created');
      setCreateFolderModalOpen(false);
      fetchFiles();
      fetchFilterOptions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create folder');
    }
  };

  // File upload
  const handleFileSelect = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    uploadFiles(Array.from(e.target.files));
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  };

  const uploadFiles = async (filesToUpload, targetFolderId = null) => {
    if (!hasPermission('kerrscreen:write')) {
      setError('You do not have permission to upload files.');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);

    const uploadFolderId = targetFolderId !== null ? targetFolderId : currentFolderId;
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (uploadFolderId) formData.append('parentFolderId', uploadFolderId);

        await kerrscreenFilesService.upload(formData, (progressEvent) => {
          const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          const overallProgress = Math.round(((i + fileProgress / 100) / filesToUpload.length) * 100);
          setUploadProgress(overallProgress);
        });
        successCount++;
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
        errors.push(`${file.name}: ${errorMessage}`);
        failCount++;
      }
    }

    setUploading(false);
    setUploadProgress(0);

    if (successCount > 0 && failCount === 0) {
      setSuccess(`Successfully uploaded ${successCount} file(s)`);
      setTimeout(() => setSuccess(null), 5000);
    } else if (failCount > 0 && successCount === 0) {
      setError(`Failed to upload:\n${errors.join('\n')}`);
    } else if (successCount > 0 && failCount > 0) {
      setSuccess(`Uploaded ${successCount} file(s)`);
      setError(`Failed: ${errors.join('\n')}`);
      setTimeout(() => setSuccess(null), 5000);
    }

    setTimeout(() => { fetchFiles(); fetchStats(); fetchFilterOptions(); }, 500);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // File drag to folder
  const handleFileDragStart = (e, file) => {
    if (file.is_folder) return;
    setDraggingFile(file);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', file.id);
  };
  const handleFileDragEnd = () => setDraggingFile(null);
  const handleFolderDragOver = (e, folderId) => { e.preventDefault(); e.stopPropagation(); setDragOverFolderId(folderId); };
  const handleFolderDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOverFolderId(null); };
  const handleFolderDrop = async (e, folder) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);

    if (draggingFile) {
      try {
        await kerrscreenFilesService.move(draggingFile.id, folder.id);
        setSuccess(`Moved "${draggingFile.original_name}" to "${folder.original_name}"`);
        setTimeout(() => setSuccess(null), 3000);
        fetchFiles();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to move file');
      }
      setDraggingFile(null);
    } else {
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) await uploadFiles(droppedFiles, folder.id);
    }
  };

  // Rename
  const openRenameModal = (file) => {
    setSelectedFile(file);
    setNewName(file.original_name);
    setRenameModalOpen(true);
  };
  const handleRename = async (e) => {
    e.preventDefault();
    if (!newName.trim()) { setError('Name cannot be empty'); return; }
    try {
      await kerrscreenFilesService.rename(selectedFile.id, newName);
      setSuccess('Renamed successfully');
      setRenameModalOpen(false);
      fetchFiles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename');
    }
  };

  // Metadata editing
  const openMetadataModal = (file) => {
    setSelectedFile(file);
    setEditCustomer(file.customer_name || '');
    setEditTags(file.tags ? file.tags.join(', ') : '');
    setEditNotes(file.notes || '');
    setMetadataModalOpen(true);
  };
  const handleSaveMetadata = async (e) => {
    e.preventDefault();
    try {
      const tags = editTags.split(',').map(t => t.trim()).filter(Boolean);
      await kerrscreenFilesService.update(selectedFile.id, {
        customerName: editCustomer,
        tags,
        notes: editNotes,
      });
      setSuccess('Metadata updated');
      setMetadataModalOpen(false);
      fetchFiles();
      fetchFilterOptions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update metadata');
    }
  };

  // Job linking
  const openLinkJobModal = (file) => {
    setSelectedFile(file);
    setLinkJobId('');
    setLinkJobModalOpen(true);
  };
  const handleLinkJob = async (e) => {
    e.preventDefault();
    if (!linkJobId) { setError('Job ID is required'); return; }
    try {
      await kerrscreenFilesService.linkToJob(selectedFile.id, linkJobId);
      setSuccess('Linked to job');
      setLinkJobModalOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to link to job');
    }
  };

  // Download
  const handleDownload = async (file) => {
    try {
      const response = await kerrscreenFilesService.download(file.id);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download file');
    }
  };

  // Delete
  const handleDelete = async (file) => {
    const itemType = file.is_folder ? 'folder' : 'file';
    if (!window.confirm(`Delete ${itemType} "${file.original_name}"? This cannot be undone.`)) return;
    try {
      await kerrscreenFilesService.delete(file.id);
      setSuccess(`${itemType} deleted`);
      fetchFiles();
      fetchStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to delete ${itemType}`);
    }
  };

  // Helpers
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (file) => {
    if (file.is_folder) return '📁';
    const ext = (file.original_name || '').split('.').pop().toLowerCase();
    const iconMap = {
      ai: '🎨', psd: '🎨', eps: '🎨', indd: '🎨', cdr: '🎨',
      pdf: '📄', png: '🖼️', jpg: '🖼️', jpeg: '🖼️', gif: '🖼️',
      svg: '🖼️', tiff: '🖼️', tif: '🖼️', webp: '🖼️', bmp: '🖼️',
    };
    return iconMap[ext] || '📁';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil(Math.abs(now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Sort files (client-side for current page)
  const sortedFiles = [...files].sort((a, b) => {
    // Folders always first
    if (a.is_folder && !b.is_folder) return -1;
    if (!a.is_folder && b.is_folder) return 1;
    if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === 'name-asc') return a.original_name.localeCompare(b.original_name);
    if (sortBy === 'name-desc') return b.original_name.localeCompare(a.original_name);
    if (sortBy === 'size-asc') return (a.size || 0) - (b.size || 0);
    if (sortBy === 'size-desc') return (b.size || 0) - (a.size || 0);
    return 0;
  });

  if (loading && files.length === 0) {
    return (
      <div className="ks-art-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading art files...</p>
        </div>
      </div>
    );
  }

  const storagePercent = stats.storageLimit > 0 ? (stats.totalSize / stats.storageLimit) * 100 : 0;

  return (
    <div className="ks-art-container">
      {/* Header */}
      <div className="ks-art-header">
        <div className="header-content">
          <h1>Kerrscreen Art Files</h1>
          <div className="header-actions">
            {hasPermission('kerrscreen:write') && (
              <>
                <button className="btn-upload" onClick={openCreateFolderModal}>
                  📁 New Folder
                </button>
                <button className="btn-upload" onClick={handleFileSelect}>
                  ⬆ Upload Art
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".ai,.psd,.eps,.pdf,.png,.jpg,.jpeg,.gif,.svg,.tiff,.tif,.indd,.cdr,.webp,.bmp"
      />

      {/* Filter Sidebar */}
      <div className="ks-art-filters">
        <div className="filter-group">
          <input
            type="text"
            className="search-input"
            placeholder="Search art files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          />
        </div>
        <div className="filter-group">
          <select value={customerFilter} onChange={(e) => { setCustomerFilter(e.target.value); handleFilterChange(); }}>
            <option value="">All Customers</option>
            {customers.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <select value={tagFilter} onChange={(e) => { setTagFilter(e.target.value); handleFilterChange(); }}>
            <option value="">All Tags</option>
            {allTags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <input
            type="text"
            placeholder="Filter by Job #"
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleFilterChange(); }}
          />
        </div>
        {(customerFilter || tagFilter || jobFilter || searchTerm) && (
          <button className="btn btn-secondary" onClick={() => {
            setCustomerFilter(''); setTagFilter(''); setJobFilter(''); setSearchTerm('');
            setPagination(prev => ({ ...prev, page: 1 }));
            setTimeout(() => fetchFiles(1), 0);
          }}>
            Clear Filters
          </button>
        )}
      </div>

      {/* Breadcrumb */}
      {folderPath.length > 0 && (
        <div className="breadcrumb-nav">
          <button onClick={() => navigateToFolder(-1)} className="breadcrumb-item">🏠 Home</button>
          {folderPath.map((folder, index) => (
            <React.Fragment key={folder.id}>
              <span className="breadcrumb-separator">/</span>
              <button onClick={() => navigateToFolder(index)} className="breadcrumb-item">
                {folder.name}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="alert alert-error" style={{ whiteSpace: 'pre-line', position: 'relative', paddingRight: '40px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ position: 'absolute', right: '12px', top: '12px', background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#721c24' }}>×</button>
        </div>
      )}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Upload Drop Zone */}
      {hasPermission('kerrscreen:write') && (
        <div
          className={`upload-dropzone ${isDragging ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleFileSelect}
        >
          <div className="upload-icon">🎨</div>
          <h3>Drag and drop art files here</h3>
          <p>AI, PSD, EPS, PDF, PNG, JPG, SVG, TIFF, InDesign</p>
          {uploading && (
            <div className="upload-progress">
              <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
              <span>{uploadProgress}%</span>
            </div>
          )}
        </div>
      )}

      {/* Storage Stats */}
      <div className="storage-stats">
        <div className="storage-header">
          <h3>Art File Storage</h3>
          <span className="storage-percent">{storagePercent.toFixed(1)}%</span>
        </div>
        <div className="storage-bar">
          <div className="storage-fill" style={{ width: `${Math.min(storagePercent, 100)}%` }}></div>
        </div>
        <div className="storage-info">
          <span>{formatFileSize(stats.totalSize)} used ({stats.totalFiles} files)</span>
          <span>{formatFileSize(stats.storageLimit)} total</span>
        </div>
      </div>

      {/* Files Section */}
      <div className="files-section">
        <div className="section-header">
          <h2>
            {currentFolderId ? folderPath[folderPath.length - 1]?.name : 'Art Files'} ({pagination.total})
          </h2>
          <div className="filter-controls">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="size-asc">Size (Smallest)</option>
              <option value="size-desc">Size (Largest)</option>
            </select>
            <div className="view-toggle">
              <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>⊞ Grid</button>
              <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>☰ List</button>
            </div>
          </div>
        </div>

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className="files-grid">
            {sortedFiles.length > 0 ? (
              sortedFiles.map((file) => (
                <div
                  key={file.id}
                  className={`file-card ${file.is_folder && dragOverFolderId === file.id ? 'drag-over-folder' : ''} ${draggingFile?.id === file.id ? 'dragging' : ''}`}
                  onDoubleClick={() => file.is_folder ? openFolder(file) : handleDownload(file)}
                  draggable={!file.is_folder && hasPermission('kerrscreen:write')}
                  onDragStart={(e) => handleFileDragStart(e, file)}
                  onDragEnd={handleFileDragEnd}
                  {...(file.is_folder && hasPermission('kerrscreen:write') ? {
                    onDragOver: (e) => handleFolderDragOver(e, file.id),
                    onDragLeave: (e) => handleFolderDragLeave(e),
                    onDrop: (e) => handleFolderDrop(e, file)
                  } : {})}
                >
                  <div className="file-actions">
                    {!file.is_folder && (
                      <>
                        <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleDownload(file); }} title="Download">⬇</button>
                        <button className="action-btn" onClick={(e) => { e.stopPropagation(); openMetadataModal(file); }} title="Edit Metadata">📋</button>
                        <button className="action-btn" onClick={(e) => { e.stopPropagation(); openLinkJobModal(file); }} title="Link to Job">🔗</button>
                      </>
                    )}
                    {hasPermission('kerrscreen:write') && (
                      <>
                        <button className="action-btn" onClick={(e) => { e.stopPropagation(); openRenameModal(file); }} title="Rename">✏️</button>
                        <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleDelete(file); }} title="Delete">🗑️</button>
                      </>
                    )}
                  </div>
                  <div className="file-icon">{getFileIcon(file)}</div>
                  <div className="file-name" title={file.original_name}>{file.original_name}</div>
                  <div className="file-meta">
                    <span>{file.is_folder ? 'Folder' : formatFileSize(file.size)}</span>
                    <span>{formatDate(file.created_at)}</span>
                  </div>
                  {file.customer_name && (
                    <div className="file-customer">{file.customer_name}</div>
                  )}
                  {file.tags && file.tags.length > 0 && file.tags[0] !== '' && (
                    <div className="file-tags">
                      {file.tags.map(tag => <span key={tag} className="tag-badge">{tag}</span>)}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                <div className="empty-icon">🎨</div>
                <h3>No Art Files Yet</h3>
                <p>Upload your first art file or create a folder to organize your work</p>
              </div>
            )}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="files-list">
            {sortedFiles.length > 0 ? (
              <table className="files-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Customer</th>
                    <th>Tags</th>
                    <th>Size</th>
                    <th>Modified</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiles.map((file) => (
                    <tr
                      key={file.id}
                      className={`${file.is_folder && dragOverFolderId === file.id ? 'drag-over-folder' : ''} ${draggingFile?.id === file.id ? 'dragging' : ''}`}
                      onDoubleClick={() => file.is_folder ? openFolder(file) : handleDownload(file)}
                      draggable={!file.is_folder && hasPermission('kerrscreen:write')}
                      onDragStart={(e) => handleFileDragStart(e, file)}
                      onDragEnd={handleFileDragEnd}
                      {...(file.is_folder && hasPermission('kerrscreen:write') ? {
                        onDragOver: (e) => handleFolderDragOver(e, file.id),
                        onDragLeave: (e) => handleFolderDragLeave(e),
                        onDrop: (e) => handleFolderDrop(e, file)
                      } : {})}
                    >
                      <td className="file-name-cell">
                        <span className="file-type-icon">{getFileIcon(file)}</span>
                        <span>{file.original_name}</span>
                      </td>
                      <td>{file.customer_name || '-'}</td>
                      <td>
                        {file.tags && file.tags.length > 0 && file.tags[0] !== ''
                          ? file.tags.map(t => <span key={t} className="tag-badge">{t}</span>)
                          : '-'}
                      </td>
                      <td>{file.is_folder ? 'Folder' : formatFileSize(file.size)}</td>
                      <td>{formatDate(file.created_at)}</td>
                      <td>
                        {file.is_folder ? (
                          <button className="action-btn" onClick={() => openFolder(file)}>Open</button>
                        ) : (
                          <>
                            <button className="action-btn" onClick={() => handleDownload(file)}>Download</button>
                            <button className="action-btn" onClick={() => openMetadataModal(file)}>Edit</button>
                            <button className="action-btn" onClick={() => openLinkJobModal(file)}>Link Job</button>
                          </>
                        )}
                        {hasPermission('kerrscreen:write') && (
                          <>
                            <button className="action-btn" onClick={() => openRenameModal(file)}>Rename</button>
                            <button className="action-btn" onClick={() => handleDelete(file)}>Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🎨</div>
                <h3>No Art Files Yet</h3>
                <p>Upload your first art file or create a folder to organize your work</p>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="pagination-controls">
            <button className="btn btn-secondary" disabled={pagination.page <= 1} onClick={() => handlePageChange(pagination.page - 1)}>Prev</button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - pagination.page) <= 2)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '...' ? (
                  <span key={`e-${idx}`} className="pagination-ellipsis">...</span>
                ) : (
                  <button key={item} className={`btn ${item === pagination.page ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handlePageChange(item)}>{item}</button>
                )
              )}
            <button className="btn btn-secondary" disabled={pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>Next</button>
            <span className="pagination-info">{pagination.total} total items</span>
          </div>
        )}
      </div>

      {/* Create Folder Modal */}
      {createFolderModalOpen && (
        <div className="modal-overlay" onClick={() => setCreateFolderModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Folder</h2>
              <button className="modal-close" onClick={() => setCreateFolderModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateFolder}>
                <div className="form-group">
                  <label>Folder Name</label>
                  <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="e.g. Customer Name - Project" autoFocus />
                </div>
                <div className="form-group">
                  <label>Customer Name (optional)</label>
                  <input type="text" value={newFolderCustomer} onChange={(e) => setNewFolderCustomer(e.target.value)} placeholder="Customer name" />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">Create</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setCreateFolderModalOpen(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {renameModalOpen && (
        <div className="modal-overlay" onClick={() => setRenameModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rename {selectedFile?.is_folder ? 'Folder' : 'File'}</h2>
              <button className="modal-close" onClick={() => setRenameModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleRename}>
                <div className="form-group">
                  <label>New Name</label>
                  <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">Rename</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setRenameModalOpen(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Metadata Modal */}
      {metadataModalOpen && selectedFile && (
        <div className="modal-overlay" onClick={() => setMetadataModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Metadata - {selectedFile.original_name}</h2>
              <button className="modal-close" onClick={() => setMetadataModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSaveMetadata}>
                <div className="form-group">
                  <label>Customer Name</label>
                  <input type="text" value={editCustomer} onChange={(e) => setEditCustomer(e.target.value)} placeholder="Customer name" />
                </div>
                <div className="form-group">
                  <label>Tags (comma-separated)</label>
                  <input type="text" value={editTags} onChange={(e) => setEditTags(e.target.value)} placeholder="logo, front, back, sleeve" />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Additional notes about this art file" rows={3} />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">Save</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setMetadataModalOpen(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Link to Job Modal */}
      {linkJobModalOpen && selectedFile && (
        <div className="modal-overlay" onClick={() => setLinkJobModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link to Job - {selectedFile.original_name}</h2>
              <button className="modal-close" onClick={() => setLinkJobModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleLinkJob}>
                <div className="form-group">
                  <label>Job ID</label>
                  <input type="number" value={linkJobId} onChange={(e) => setLinkJobId(e.target.value)} placeholder="Enter Kerrscreen Job ID" autoFocus />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">Link</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setLinkJobModalOpen(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
